import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// 1. Ant Design
import { Button, Card, Col, Descriptions, message, Result, Row, Skeleton, Tabs, Tag, Tooltip, Typography } from "antd";
import { ArrowLeftOutlined, ReloadOutlined } from "@ant-design/icons";
// 2. External libraries
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
// 3. Internal components
import AdminsTab from "@/components/Company/AdminsTab";
import ReportTab from "@/components/Company/ReportTab";
import CompanyRequisitesForm from "@/components/Company/CompanyRequisitesForm";
import EmployeesTab from "@/components/Company/EmployeesTab";
import { COMPANY_TABS, companyTabPath } from "@/components/Company/companyTabs";
import SettingsTab from "@/components/Company/SettingsTab";
import SettlementsTab from "@/components/Company/SettlementsTab";
import TariffsTab from "@/components/Company/TariffsTab";
// 4. Stores
import { getRootStore } from "@/stores/RootStore";
// 5. Types, utils, styles
import { CORP_COMPANY_STATUS_COLORS, CREDIT_STATE_COLORS, CREDIT_STATE_LABELS, } from "@/types/company/company.common";
import { CORP_COMPANY_STATUS_LABELS } from "@/types/corp/corpSession.common";
import { canManageCompanies, hasPermission } from "@/utils/permissions";
import styles from "@/styles/CompanyPage.module.less";
const { Title, Text } = Typography;
const formatDateTime = (value) => value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "—";
/** Содержимое вкладки; рендерится только активная вкладка. */
const renderTab = (tab) => {
    switch (tab) {
        case "settings":
            return _jsx(SettingsTab, {});
        case "tariffs":
            return _jsx(TariffsTab, {});
        case "employees":
            return _jsx(EmployeesTab, {});
        case "admins":
            return _jsx(AdminsTab, {});
        case "settlements":
            return _jsx(SettlementsTab, {});
        case "report":
            return _jsx(ReportTab, {});
        default:
            return null;
    }
};
/** `/companies/new` (UC1112, P-2): создание компании; финансовые поля не задаются (значения по умолчанию). */
const CompanyCreateView = observer(() => {
    const navigate = useNavigate();
    const { authStore, singleCompanyStore: store } = getRootStore();
    useEffect(() => {
        store.clearCreateError();
    }, []);
    const handleSubmit = async (requisites) => {
        const result = await store.createCompany(requisites);
        if (!result)
            return false;
        message.success(`Компания создана. Тариф скопирован с общего на ${dayjs().format("DD.MM.YYYY")}`);
        navigate(companyTabPath(result.companyId), { replace: true });
        return true;
    };
    return (_jsxs("div", { className: styles.container, children: [_jsx("div", { className: styles.header__top, children: _jsx(Button, { icon: _jsx(ArrowLeftOutlined, {}), onClick: () => navigate("/companies"), children: "\u041A \u0441\u043F\u0438\u0441\u043A\u0443 \u043A\u043E\u043C\u043F\u0430\u043D\u0438\u0439" }) }), _jsx(Card, { title: "\u041D\u043E\u0432\u0430\u044F \u043A\u043E\u043C\u043F\u0430\u043D\u0438\u044F", children: _jsx(CompanyRequisitesForm, { mode: "create", initial: null, canEdit: canManageCompanies(authStore.userRole), isSaving: store.isSaving, error: store.createError, onSubmit: handleSubmit, onCancel: () => navigate("/companies") }) })] }));
});
CompanyCreateView.displayName = "CompanyCreateView";
/** `/companies/:id/:tab` (UC1113): шапка компании и адресуемые вкладки. */
const CompanyCard = observer(({ companyId, tab }) => {
    const navigate = useNavigate();
    const { authStore, singleCompanyStore: store } = getRootStore();
    useEffect(() => {
        void store.init(companyId);
    }, [companyId]);
    useEffect(() => () => {
        store.dispose();
    }, []);
    const company = store.company;
    if (store.notFound) {
        return (_jsx(Result, { status: "404", title: "\u041A\u043E\u043C\u043F\u0430\u043D\u0438\u044F \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u0430", extra: _jsx(Button, { type: "primary", onClick: () => navigate("/companies"), children: "\u041A \u0441\u043F\u0438\u0441\u043A\u0443 \u043A\u043E\u043C\u043F\u0430\u043D\u0438\u0439" }) }));
    }
    if (!company && store.loadError && !store.isLoading) {
        return (_jsx(Result, { status: "error", title: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u043A\u043E\u043C\u043F\u0430\u043D\u0438\u044E", subTitle: store.loadError, extra: [
                _jsx(Button, { type: "primary", onClick: () => void store.load(), children: "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C" }, "retry"),
                _jsx(Button, { onClick: () => navigate("/companies"), children: "\u041A \u0441\u043F\u0438\u0441\u043A\u0443 \u043A\u043E\u043C\u043F\u0430\u043D\u0438\u0439" }, "list"),
            ] }));
    }
    if (!company || company.id !== companyId) {
        return (_jsx("div", { className: styles.container, children: _jsx(Card, { children: _jsx(Skeleton, { active: true, paragraph: { rows: 6 } }) }) }));
    }
    const tabItems = COMPANY_TABS.filter((item) => hasPermission(authStore.userRole, item.permission)).map((item) => ({
        key: item.key,
        label: _jsx("span", { "data-testid": item.testId, children: item.label }),
    }));
    const statusTag = (_jsx(Tag, { color: CORP_COMPANY_STATUS_COLORS[company.status], "data-testid": "corp-company-status", children: CORP_COMPANY_STATUS_LABELS[company.status] }));
    return (_jsxs("div", { className: styles.container, children: [_jsxs(Card, { children: [_jsxs("div", { className: styles.header__top, children: [_jsx(Button, { icon: _jsx(ArrowLeftOutlined, {}), onClick: () => navigate("/companies"), children: "\u041A \u0441\u043F\u0438\u0441\u043A\u0443 \u043A\u043E\u043C\u043F\u0430\u043D\u0438\u0439" }), _jsx(Button, { icon: _jsx(ReloadOutlined, {}), onClick: () => void store.refresh(), loading: store.isLoading, children: "\u041E\u0431\u043D\u043E\u0432\u0438\u0442\u044C" })] }), _jsxs("div", { className: `${styles.header__title} ${styles.header__stats}`, children: [_jsx(Title, { level: 3, children: company.name }), company.statusReason ? (_jsx(Tooltip, { title: `${company.statusReason} · ${formatDateTime(company.statusChangedAt)}`, children: statusTag })) : (statusTag), _jsx(Tag, { color: CREDIT_STATE_COLORS[company.creditState], "data-testid": "corp-company-credit-state", children: CREDIT_STATE_LABELS[company.creditState] })] }), company.status !== "ACTIVE" && company.statusReason ? (_jsxs(Text, { type: "secondary", children: ["\u041F\u0440\u0438\u0447\u0438\u043D\u0430: ", company.statusReason] })) : null, _jsx(Row, { className: styles.header__stats, children: _jsx(Col, { span: 24, children: _jsxs(Descriptions, { size: "small", column: { xs: 1, sm: 2, lg: 4 }, children: [_jsx(Descriptions.Item, { label: "\u0410\u043A\u0442\u0438\u0432\u043D\u044B\u0435 \u043F\u0440\u0438\u0432\u044F\u0437\u043A\u0438", children: company.activeLinksCount }), _jsx(Descriptions.Item, { label: "\u041A\u043E\u0440\u043F-\u0430\u0434\u043C\u0438\u043D\u043E\u0432", children: company.adminsCount }), _jsx(Descriptions.Item, { label: "\u041D\u0435\u0437\u0430\u0432\u0435\u0440\u0448\u0451\u043D\u043D\u044B\u0435 \u043A\u043E\u0440\u043F-\u0437\u0430\u043A\u0430\u0437\u044B", children: company.openOrdersCount }), _jsx(Descriptions.Item, { label: "\u0421\u043E\u0437\u0434\u0430\u043D\u0430", children: formatDateTime(company.createdAt) })] }) }) })] }), _jsx(Tabs, { className: styles.tabs, activeKey: tab, items: tabItems, onChange: (key) => navigate(companyTabPath(companyId, key)) }), renderTab(tab)] }));
});
CompanyCard.displayName = "CompanyCard";
/**
 * Карточка корпоративного клиента (FR-006): `/companies/new` — создание (UC1112),
 * `/companies/:id/<tab>` — карточка с адресуемыми вкладками (UC1113). Маршруты и права — AppRouter + companyTabs.
 */
const CompanyPage = ({ tab }) => {
    const { id } = useParams();
    if (!id)
        return _jsx(CompanyCreateView, {});
    if (!tab)
        return _jsx(Navigate, { to: companyTabPath(id), replace: true });
    return _jsx(CompanyCard, { companyId: id, tab: tab });
};
CompanyPage.displayName = "CompanyPage";
export default CompanyPage;
